import {
  Autocomplete,
  darken,
  FormControlLabel,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  styled,
  Switch,
  TextField,
} from "@mui/material";
import { cnpj, cpf } from "cpf-cnpj-validator";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { isEmail, mask, unmaskMoney } from "../../functions";

export const DocumentInput = ({
  control,
  name = "document",
  label = "CPF/CNPJ",
  defaultValue = "",
  required = false,
  ...other
}) => {
  const validarCpfCnpj = (value) => {
    const raw = value.replace(/\D/g, "");
    if (raw.length === 11) return cpf.isValid(raw);
    if (raw.length === 14) return cnpj.isValid(raw);
    return false;
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: "Campo obrigatório",
          pattern: {
            value:
              /^(\d{3}\.\d{3}\.\d{3}-\d{2}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/,
            message: "CPF/CNPJ inválido",
          },
          validate: (value) => validarCpfCnpj(value) || "CPF/CNPJ inválido",
        }}
        render={({ field, fieldState }) => {
          // const validate =
          //   field.value.length > 0 && !validarCpfCnpj(field.value);
          // field.value = mask(field, "cpfcnpj");
          // field.label = label;

          return (
            <>
              <TextField
                {...field}
                required={required}
                {...other}
                onChange={(e) => {
                  e.target.value = mask(e.target, "cpfcnpj");
                  field.onChange(e);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                label={label}
                slotProps={{ htmlInput: { minLength: 14, maxLength: 15 } }}
                inputProps={{
                  inputMode: "numeric",
                  //pattern: "[0-9]*",
                }}
              />
            </>
          );
        }}
      />
    </>
  );
};

export const PhoneInput = ({
  control,
  name = "phone",
  label = "Telefone",
  defaultValue = "",
  required = false,
  ...other
}) => {
  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: "Telefone é obrigatório",
          pattern: {
            value: /^\(\d{2}\)\s?\d{4,5}-\d{4}$/,
            message: "Formato inválido (XX) XXXX-XXXX",
          },
        }}
        render={({ field, fieldState }) => {
          //const validate = !!field.value.length && field.value.length < 14;

          return (
            <>
              <TextField
                {...field}
                required={required}
                {...other}
                onChange={(e) => {
                  e.target.value = mask(e.target, "telefone");
                  field.onChange(e);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{ htmlInput: { maxLength: 15, minLength: 13 } }}
                label={label}
              />
            </>
          );
        }}
      />
    </>
  );
};

export const DateInput = ({
  control,
  name = "date",
  label = "Data",
  defaultValue = "",
  required = false,
  ...other
}) => {
  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: "Data é obrigatória",
          pattern: {
            value: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
            message: "Formato inválido (DD/MM/AAAA)",
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <TextField
              {...field}
              required={required}
              {...other}
              onChange={(e) => {
                e.target.value = mask(e.target, "data");
                field.onChange(e);
              }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{ htmlInput: { maxLength: 10 } }}
              label={label}
            />
          );
        }}
      />
    </>
  );
};

export const EmailInput = ({
  control,
  name = "email",
  label = "E-mail",
  defaultValue = "",
  required = false,
  ...other
}) => {
  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => {
          field.label = label;
          const validate = !!field.value.length && !isEmail(field.value);

          return (
            <>
              <TextField
                error={validate}
                {...field}
                required={required}
                {...other}
                helperText={validate && "E-mail inválido"}
              />
            </>
          );
        }}
      />
    </>
  );
};

export const CEPInput = ({
  control,
  name = "address.cep",
  label = "CEP",
  defaultValue = "",
  required = false,
  onAddressFound,
  ...other
}) => {
  let lastCep;
  const getAddress = (cep) => {
    const rawCep = cep?.replace(/\D/g, "");

    if (lastCep === rawCep) return;
    lastCep = rawCep;

    const valid = rawCep?.length === 8;

    if (valid && typeof onAddressFound === "function") {
      fetch(`https://viacep.com.br/ws/${rawCep}/json/`)
        .then((res) => res.json())
        .then((data) => {
          if (data.erro) throw new Error(data.erro);

          const {
            bairro: neighborhood,
            cep,
            estado: state,
            localidade: city,
            logradouro: street,
            uf,
          } = data;

          const address = { neighborhood, cep, state, city, street, uf };

          onAddressFound(address);
        })
        .catch((err) => {
          //console.error("Erro ao buscar CEP:", err);
        });
    }
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => {
          const validate = !!field.value.length && field.value.length < 8;
          field.value = mask(field, "cep");
          field.label = label;

          field.onBlur = () => getAddress(field.value);

          return (
            <>
              <TextField
                error={validate}
                {...field}
                required={required}
                {...other}
                helperText={validate && "CEP inválido"}
              />
            </>
          );
        }}
      />
    </>
  );
};

export const MoneyInput = ({
  control,
  name = "value",
  label = "Valor",
  defaultValue = "",
  required = false,
  helperText,
  ...other
}) => {
  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => {
          if (field.value) {
            field.value = mask(field, "moeda");
          }
          field.label = label;
          const uValue = unmaskMoney(field.value);

          const validate = field.value && uValue === 0;

          return (
            <>
              <TextField
                error={validate}
                {...field}
                required={required}
                {...other}
                helperText={validate && (helperText || "Valor inválido")}
                size="small"
              />
            </>
          );
        }}
      />
    </>
  );
};

export const TextFieldComponent = ({
  name,
  control,
  label,
  required,
  defaultValue = "",
  ...other
}) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    render={({ field }) => (
      <TextField
        {...field}
        label={label}
        required={required}
        size="small"
        {...other}
      />
    )}
  />
);

export const TextFieldPasswordComponent = ({
  name = "password",
  control,
  label,
  required,
  defaultValue = "",
  ...other
}) => {
  const [showPassword, handleShowPassword] = useState(false);
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <TextField
          {...field}
          label={label}
          required={required}
          type={showPassword ? "text" : "password"}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleShowPassword((s) => !s)}
                    edge="end"
                  >
                    <Icon>
                      {showPassword ? "visibility" : "visibility_off"}
                    </Icon>
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          size="small"
          {...other}
        />
      )}
    />
  );
};

export const SelectComponent = ({
  values = [],
  name,
  control,
  label,
  required,
  defaultValue = "",
  ...other
}) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    render={({ field }) => (
      <Select {...field} label={label} required={required} {...other}>
        {values.map((prod) => (
          <MenuItem key={prod.value} value={prod.value}>
            {prod.label}
          </MenuItem>
        ))}
      </Select>
    )}
  />
);
export const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: "#fff",
  //backgroundColor: "#004",
  ...theme.applyStyles("dark", {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

export const GroupItems = styled("ul")({
  padding: 0,
});

export const SelectWithSearchComponent = ({
  values = [],
  name,
  control,
  label,
  required,
  defaultValue = "",
  margin = "normal",
  onTextChange = () => {},
  ...other
}) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    render={({ field, fieldState }) => (
      <Autocomplete
        {...field}
        options={values}
        //getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        onChange={(_, data) => {
          field.onChange(data);
        }}
        size="small"
        {...other}
        renderInput={(params) => (
          <TextField
            {...params}
            onChange={(e) => onTextChange(e.target.value)}
            margin={margin}
            label={label}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            // slotProps={{
            //   input: { size: "small" },
            // }}
          />
        )}
      />
    )}
    rules={{ required: "Campo obrigatório" }}
  />
);

export const RadioGroupComponent = ({
  values = [],
  name,
  control,
  label,
  required,
  defaultValue = "",
  row = true,
  ...other
}) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    render={({ field }) => (
      <RadioGroup {...field} defaultValue={defaultValue} name={name} row={row}>
        {values.map(({ label, value }, key) => (
          <FormControlLabel
            key={key}
            value={value}
            control={<Radio required={required} />}
            label={label}
          />
        ))}
      </RadioGroup>
    )}
  />
);

export const SwitchComponent = ({
  value = false,
  name,
  control,
  label,
  required,
  defaultValue = "",
  row = true,
  ...other
}) => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    render={({ field }) => (
      <FormControlLabel
        value={value}
        control={<Switch required={required} />}
        label={label}
      />
    )}
  />
);
